import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Play, Pause, Maximize2 } from 'lucide-react';

interface CinematicProfileAvatarProps {
    heroFrame: string;
    videoUrl?: string;
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
    isExpanded?: boolean;
    onExpand?: () => void;
    className?: string;
    trimStart?: number;
    trimEnd?: number;
}

export function CinematicProfileAvatar({
    heroFrame,
    videoUrl,
    size = 'md',
    isExpanded = false,
    onExpand,
    className = "",
    trimStart,
    trimEnd
}: CinematicProfileAvatarProps) {
    const [showVideo, setShowVideo] = useState(false);
    const [isShimmering, setIsShimmering] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);

    const sizeClasses = {
        sm: 'w-12 h-12',
        md: 'w-24 h-24',
        lg: 'w-32 h-32',
        xl: 'w-48 h-48',
        full: 'w-full h-full'
    };

    // Trigger shimmer and then video when expanded
    useEffect(() => {
        if (isExpanded && videoUrl) {
            setIsShimmering(true);
            const timer = setTimeout(() => {
                setIsShimmering(false);
                setShowVideo(true);
            }, 1000); // 1s shimmer
            return () => clearTimeout(timer);
        } else {
            setShowVideo(false);
            setIsShimmering(false);
        }
    }, [isExpanded, videoUrl]);

    const handleVideoEnd = () => {
        if (videoRef.current) {
            videoRef.current.currentTime = 0;
            videoRef.current.play();
        }
    };

    return (
        <div 
            className={`relative overflow-hidden rounded-full cursor-pointer group transition-all duration-500 ${sizeClasses[size]} ${className}`}
            onClick={onExpand}
        >
            {/* Base Image (Hero Frame) */}
            <img 
                src={heroFrame} 
                alt="Profile" 
                className={`w-full h-full object-cover transition-opacity duration-700 ${showVideo ? 'opacity-0' : 'opacity-100'}`}
            />

            {/* Shimmer Overlay */}
            <AnimatePresence>
                {isShimmering && (
                    <motion.div
                        initial={{ x: '-100%', skewX: -20 }}
                        animate={{ x: '200%', skewX: -20 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 1, ease: "easeInOut" }}
                        className="absolute inset-0 z-10 bg-linear-to-r from-transparent via-white/40 to-transparent pointer-events-none"
                        style={{
                            background: 'linear-gradient(115deg, transparent 0%, rgba(255,215,0,0.4) 30%, rgba(255,255,255,0.6) 50%, rgba(192,192,192,0.4) 70%, transparent 100%)'
                        }}
                    />
                )}
            </AnimatePresence>

            {/* Video Loop */}
            {showVideo && videoUrl && (
                <motion.video
                    ref={videoRef}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    src={videoUrl}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="absolute inset-0 w-full h-full object-cover z-0"
                    onTimeUpdate={(e) => {
                        if (trimStart !== undefined && trimEnd !== undefined) {
                            const vid = e.currentTarget;
                            if (vid.currentTime >= trimEnd || vid.currentTime < trimStart) {
                                vid.currentTime = trimStart;
                            }
                        }
                    }}
                    onLoadedData={(e) => {
                        if (trimStart !== undefined) {
                            e.currentTarget.currentTime = trimStart;
                        }
                    }}
                />
            )}

            <div className="absolute inset-0 pointer-events-none bg-linear-to-t from-black/80 via-transparent to-black/30 mix-blend-multiply" />
            
            {/* Glassmorphism Details */}
            {!isExpanded && (
                <div className="absolute bottom-3 right-3 p-2 rounded-full bg-black/50 backdrop-blur-xl border border-white/20 shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-90 group-hover:scale-100">
                    <Maximize2 className="w-3.5 h-3.5 text-white/95" />
                </div>
            )}
            
            {/* Gold Accent (Cinematic touch) */}
            <div className="absolute top-0 left-0 w-1 h-8 bg-linear-to-b from-amber-500/80 to-transparent" />
            <div className="absolute bottom-0 right-0 w-8 h-1 bg-linear-to-l from-amber-500/80 to-transparent" />
        </div>
    );
}
